import { FinWordGameState, FinWordStats, QuizState, QuizStats, DailyChallengeState } from "./types";
import { getCurrentDateUTC, getDaysBetween } from "./dateUtils";

const PREFIX = "daily_";

export type StorageResult<T> =
  | { success: true; data: T }
  | { success: false; error: Error };

function safeGetItem(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(PREFIX + key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): StorageResult<void> {
  if (typeof window === "undefined") {
    return { success: false, error: new Error("Window is undefined") };
  }
  try {
    localStorage.setItem(PREFIX + key, value);
    return { success: true, data: undefined };
  } catch (error) {
    const isQuotaExceeded =
      error instanceof DOMException &&
      (error.name === "QuotaExceededError" ||
        error.name === "NS_ERROR_DOM_QUOTA_REACHED" ||
        error.code === 22 ||
        error.code === 1014);
    if (isQuotaExceeded) {
      // Clear old data and try again
      try {
        clearOldStorageData();
        localStorage.setItem(PREFIX + key, value);
        return { success: true, data: undefined };
      } catch (retryError) {
        return {
          success: false,
          error: new Error("LocalStorage quota exceeded even after cleanup"),
        };
      }
    }
    return { success: false, error: error instanceof Error ? error : new Error(String(error)) };
  }
}

function clearOldStorageData(): void {
  if (typeof window === "undefined") return;
  // Look for keys with the prefix and clean up non-essential historical game states
  const today = getCurrentDateUTC();
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    // Remove individual day states from older dates if we need to free space.
    // Today's key is exempt — a quota event mid-game must not wipe the
    // player's in-progress state.
    if (key && key.startsWith(PREFIX) && !key.endsWith("stats") && !key.endsWith("hub_state") && !key.includes(today)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {}
  });
}

/**
 * Practice sessions persist under unique `*_state_practice_<ts>_<rand>` keys
 * (one orphan per session). Called when a new practice session starts so old
 * ones never accumulate.
 */
export function purgePracticeStates(): void {
  if (typeof window === "undefined") return;
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(PREFIX) && key.includes("_state_practice_")) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {}
  });
}

// 1. FinWord Storage helpers
export function loadFinWordState(date: string): FinWordGameState | null {
  const raw = safeGetItem(`finword_state_${date}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.date === "string" && Array.isArray(parsed.guesses)) {
      return parsed as FinWordGameState;
    }
  } catch {
    // Corrupted state
  }
  return null;
}

export function saveFinWordState(state: FinWordGameState): StorageResult<void> {
  return safeSetItem(`finword_state_${state.date}`, JSON.stringify(state));
}

export function loadFinWordStats(): FinWordStats {
  const raw = safeGetItem("finword_stats");
  const defaultStats: FinWordStats = {
    gamesPlayed: 0,
    gamesWon: 0,
    currentStreak: 0,
    maxStreak: 0,
    guessDistribution: [0, 0, 0, 0, 0, 0],
    averageGuesses: 0,
  };
  if (!raw) return defaultStats;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.gamesPlayed === "number" && Array.isArray(parsed.guessDistribution)) {
      return parsed as FinWordStats;
    }
  } catch {}
  return defaultStats;
}

export function saveFinWordStats(stats: FinWordStats): StorageResult<void> {
  return safeSetItem("finword_stats", JSON.stringify(stats));
}

// 2. Quiz Storage helpers
export function loadQuizState(date: string): QuizState | null {
  const raw = safeGetItem(`quiz_state_${date}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.date === "string" && parsed.answers) {
      return parsed as QuizState;
    }
  } catch {}
  return null;
}

export function saveQuizState(state: QuizState): StorageResult<void> {
  return safeSetItem(`quiz_state_${state.date}`, JSON.stringify(state));
}

export function loadQuizStats(): QuizStats {
  const raw = safeGetItem("quiz_stats");
  const defaultStats: QuizStats = {
    quizzesTaken: 0,
    totalQuestions: 0,
    correctAnswers: 0,
    currentStreak: 0,
    maxStreak: 0,
    averageScore: 0,
    categoryPerformance: {
      tax: 0,
      investing: 0,
      loans: 0,
      savings: 0,
      retirement: 0,
      budgeting: 0,
      general: 0,
    },
  };
  if (!raw) return defaultStats;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.quizzesTaken === "number") {
      return parsed as QuizStats;
    }
  } catch {}
  return defaultStats;
}

export function saveQuizStats(stats: QuizStats): StorageResult<void> {
  return safeSetItem("quiz_stats", JSON.stringify(stats));
}

// 5. Daily Hub Overall state helpers
export function loadDailyChallengeState(): DailyChallengeState {
  const today = getCurrentDateUTC();
  const raw = safeGetItem("hub_state");
  const defaultState: DailyChallengeState = {
    date: today,
    wordCompleted: false,
    quizCompleted: false,
    streak: 0,
  };

  if (!raw) return defaultState;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.date === "string") {
      if (parsed.date !== today) {
        // Judge streak continuity from the last day BOTH games were completed.
        // parsed.date is merely the last day the state was touched — keying
        // the gap off it let a half-completed day preserve a broken chain.
        const anchor = typeof parsed.lastCompletedDate === "string" ? parsed.lastCompletedDate : parsed.date;
        const gap = getDaysBetween(anchor, today);
        let currentStreak = parsed.streak || 0;
        if (gap > 1) {
          currentStreak = 0;
        }
        return {
          date: today,
          wordCompleted: false,
          quizCompleted: false,
          streak: currentStreak,
          lastCompletedDate: parsed.lastCompletedDate,
        };
      }
      return parsed as DailyChallengeState;
    }
  } catch {}

  return defaultState;
}

export function saveDailyChallengeState(state: DailyChallengeState): StorageResult<void> {
  return safeSetItem("hub_state", JSON.stringify(state));
}

